import moment from 'moment';

export const MIN_LEAD_MINUTES = 20;
export const MAX_DAYS_AHEAD = 35;
export const MAX_MESSAGE_LENGTH = 1600;

export const SCHEDULE_ERROR = `Choose a time between ${MIN_LEAD_MINUTES} minutes and ${MAX_DAYS_AHEAD} days from now`;

export type PickerMode = 'date' | 'time';

// Now + 30 minutes, rounded up to the next 5 minutes (local time)
export function getDefaultScheduleDateTime() {
  const date = moment()
    .add(30, 'minutes')
    .seconds(0)
    .milliseconds(0);
  const remainder = date.minutes() % 5;
  if (remainder) date.add(5 - remainder, 'minutes');
  return date;
}

export function getMaximumScheduleDate() {
  return moment().add(MAX_DAYS_AHEAD, 'days');
}

// Validated against local "now"
export function isValidScheduleDate(date: moment.Moment) {
  return (
    date.isSameOrAfter(moment().add(MIN_LEAD_MINUTES, 'minutes')) &&
    date.isSameOrBefore(getMaximumScheduleDate())
  );
}

// A scheduled message can be changed until MIN_LEAD_MINUTES before it's sent
export function isEditableScheduledMessage(scheduledAt?: string | null) {
  return (
    !!scheduledAt &&
    moment()
      .add(MIN_LEAD_MINUTES, 'minutes')
      .isBefore(moment.utc(scheduledAt))
  );
}

// Applies only the date or the time part of `picked` onto `current`
export function mergeDatePart(
  current: moment.Moment,
  picked: moment.Moment,
  mode: PickerMode
) {
  const result = current.clone();
  if (mode === 'date') {
    result.set({
      year: picked.year(),
      month: picked.month(),
      date: picked.date(),
    });
  } else {
    result.set({ hour: picked.hour(), minute: picked.minute() });
  }
  return result;
}

// Local-time label such as "Today at 6:00 PM", "Tomorrow at 9:00 AM" or "Tue, Oct 20 at 9:00 AM"
export function formatRelativeDateTime(datetime: string) {
  const dateMoment = moment.utc(datetime).local();
  const now = moment();
  const time = dateMoment.format('h:mm A');
  if (dateMoment.isSame(now, 'day')) return `Today at ${time}`;
  if (dateMoment.isSame(now.clone().add(1, 'day'), 'day')) {
    return `Tomorrow at ${time}`;
  }
  return `${dateMoment.format('ddd, MMM D')} at ${time}`;
}
